// WritingArea.js - Final version aligned with scenarios_writing table
import React, { useState } from "react";
import {
  FileText,
  Edit3,
  Eye,
  EyeOff,
  BookOpen,
  RefreshCw,
} from "lucide-react";

const WritingArea = ({
  writingText,
  setWritingText,
  writingAttempt,
  wordCount,
  isPlagiarized,
  handleSubmitWriting,
  currentScenarioData,
  currentScenario,
  testId = "test1",
  isSecondAttempt = false,
  firstAttemptText = "",
  firstAttemptResults = null,
  viewMode = false,
}) => {
  const [outline, setOutline] = useState("");
  const [showOutlineInstruction, setShowOutlineInstruction] = useState(true);

  /** 🧩 Mapping data from DB fields **/
  const outlineInstruction =
    currentScenarioData?.outline_instruction || "";
  const outlinePlaceholder =
    currentScenarioData?.outline_placeholder || "Write your outline here...";
  const writingInstruction =
    currentScenarioData?.writing_instruction ||
    currentScenarioData?.instruction_intro ||
    currentScenarioData?.writing_title ||
    "";
  const writingPlaceholder =
    currentScenarioData?.writing_placeholder || "Type your writing here...";
  const submissionNote = currentScenarioData?.submission_note || "";
  const instructionIntro =
    currentScenarioData?.instruction_intro ||
    currentScenarioData?.instruction_additional ||
    "";
  const instructionVideo = currentScenarioData?.instruction_video || null;

  /** Word Count helper */
  const getWordCountForText = (text) =>
    text.trim().length === 0
      ? 0
      : text.trim().split(/\s+/).filter(Boolean).length;

  /** Instruction section (Hide/Show) */
  const renderOutlineInstruction = () => {
    // ✅ Only hide if there’s no instruction content at all
    if (!instructionIntro && !instructionVideo) return null;

    return (
      <div className="bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 rounded-xl p-6 mb-6 border border-purple-200 shadow-lg">
        {/* Header with toggle */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <BookOpen className="w-6 h-6 text-purple-600 mr-3" />
            <h3 className="text-xl font-bold text-gray-800">
              {currentScenarioData?.writing_title ||
                "Writing Outline Instructions"}
            </h3>
          </div>
          <button
            onClick={() => setShowOutlineInstruction(!showOutlineInstruction)}
            className="text-purple-600 hover:text-purple-800 text-sm flex items-center space-x-1 px-3 py-1 rounded-lg border border-purple-200 hover:bg-purple-50 transition-colors"
          >
            {showOutlineInstruction ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            <span>{showOutlineInstruction ? "Hide" : "Show"} instruction</span>
          </button>
        </div>

        {showOutlineInstruction && (
          <div className="flex space-x-6">
            {/* Image or video preview */}
            <div className="w-[30%] flex-shrink-0">
              <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl overflow-hidden shadow-lg border border-gray-200">
                {instructionVideo ? (
                  <img
                    src={`/AI_Video/${instructionVideo}`}
                    alt="Instruction visual"
                    className="w-full h-auto rounded-xl object-contain bg-white"
                    style={{ minHeight: "150px", maxHeight: "250px" }}
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextElementSibling.style.display = "flex";
                    }}
                  />
                ) : null}

                {/* fallback */}
                <div className="hidden w-full h-48 border border-gray-300 rounded bg-purple-50 items-center justify-center">
                  <div className="text-center p-4">
                    <div className="text-4xl mb-2">📝</div>
                    <p className="text-gray-600 font-medium">Outline</p>
                    <p className="text-sm text-gray-500">Instructions</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Text content */}
            <div className="flex-1">
              <div className="bg-white bg-opacity-70 rounded-xl p-5 shadow-sm border border-purple-100 h-full">
                <div className="text-gray-700 text-sm leading-relaxed">
                  <p>{instructionIntro}</p>
                </div>

                <div className="mt-4 bg-purple-50 border-l-4 border-purple-400 p-3 rounded">
                  <p className="text-purple-800 font-medium text-sm">
                    💡 <strong>Tip:</strong> A clear outline helps structure your
                    writing and ensures you cover all important points.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  /** Header for writing */
  const writingHeader = writingInstruction;

  /** Outline word counter */
  const outlineWordCount =
    outline.trim().length === 0
      ? 0
      : outline.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="space-y-6">
      {/* Instruction Section */}
      {renderOutlineInstruction()}

      {/* First Attempt Section */}
      {isSecondAttempt && firstAttemptText && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-medium text-yellow-800 mb-3 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            📝 Your First Attempt (Reference)
          </h4>
          <div className="bg-white border border-yellow-200 rounded-lg p-4">
            <div className="max-h-32 overflow-y-auto text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
              {firstAttemptText}
            </div>
            <div className="flex justify-between items-center mt-3 pt-3 border-t border-yellow-200">
              <span className="text-xs text-yellow-700">
                Word count: {getWordCountForText(firstAttemptText)} | Score:{" "}
                {firstAttemptResults?.score || "N/A"}/10
              </span>
              <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded">
                Version 1 - For Reference
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Second Attempt Banner */}
      {isSecondAttempt && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <RefreshCw className="w-5 h-5 text-green-600 mr-2" />
            <h3 className="text-lg font-semibold text-green-700">
              Second Attempt - Writing Version 2
            </h3>
          </div>
          <p className="text-sm text-green-600 mt-1">
            Revise your writing task in the space below. Avoid plagiarism from
            sample responses.
          </p>
        </div>
      )}

      {/* Outline Section */}
      {!isSecondAttempt && currentScenarioData?.outline_enabled !== 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <h4 className="font-semibold text-blue-800 flex items-center">
              <Edit3 className="w-5 h-5 mr-2" />
              📝 Writing Outline
            </h4>
          </div>

          <p className="text-sm text-blue-700 mb-3">
            {outlineInstruction}
          </p>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="block text-sm font-medium text-gray-700">
                Your Outline
              </label>
              <span className="text-xs text-gray-500">
                {outlineWordCount} words
              </span>
            </div>

            <textarea
              value={outline}
              onChange={(e) => !viewMode && setOutline(e.target.value)}
              placeholder={outlinePlaceholder}
              disabled={viewMode}
              className={`w-full border border-gray-300 rounded-lg p-3 min-h-60 ${viewMode ? "bg-gray-100 cursor-not-allowed opacity-70" : ""}`}
            />
          </div>
        </div>
      )}


      {/* Main Writing Area */}
      <div className="space-y-4">
        {(
          <div className="mb-2">
            <h4 className="font-semibold text-gray-800">✏️ Your Writing</h4>
            <p className="text-sm text-blue-700 mt-1">{writingHeader}</p>
          </div>
        )}

        {/* Show outline reference */}
        {outline.trim() && (
          <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded-lg">
            <h5 className="font-medium text-green-800 mb-2 text-sm">
              📋 Your Outline Reference:
            </h5>
            <div className="bg-white rounded p-2 border border-green-200">
              <pre className="whitespace-pre-wrap text-xs text-gray-700 max-h-24 overflow-y-auto">
                {outline}
              </pre>
            </div>
          </div>
        )}

        {/* Writing Text Area */}
        <div className="space-y-2">
          <textarea
            value={writingText}
            onChange={(e) => !viewMode && setWritingText(e.target.value)}
            placeholder={writingPlaceholder}
            disabled={viewMode}
            className={`w-full border border-gray-300 rounded-lg p-3 min-h-60 ${viewMode ? "bg-gray-100 cursor-not-allowed opacity-70" : ""}`}
          />

          {/* Footer bar */}
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-4 text-gray-600">
              <span>Word count: {wordCount}</span>
              {outline.trim() && (
                <span className="text-green-600 text-xs">
                  ✓ Outline available
                </span>
              )}
              {isSecondAttempt && (
                <span className="text-blue-600 text-xs">🔄 Version 2</span>
              )}
            </div>

            <button
              onClick={viewMode ? undefined : handleSubmitWriting}
              disabled={viewMode}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${viewMode
                ? "bg-gray-400 text-white cursor-not-allowed"
                : isSecondAttempt
                  ? "bg-green-600 text-white hover:bg-green-700"
                  : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
            >
              {viewMode
                ? "👀 View Only"
                : isSecondAttempt
                  ? "Submit Final Version"
                  : "Submit Version 1"}
            </button>
          </div>

          {isPlagiarized && (
            <div className="text-red-600 text-sm mt-2 p-2 bg-red-50 rounded border border-red-200">
              ⚠️ Content similar to sample detected, your writing may receive 0
              points.
            </div>
          )}

          {/* Submission Note */}
          {submissionNote && (
            <div className="text-gray-700 text-xs mt-3 italic">
              📝 {submissionNote}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WritingArea;
